import { useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { SlideOver } from '@/components/ui/SlideOver';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PaymentTypeBadge } from '@/components/ui/PaymentTypeBadge';
import { SourceBadge } from '@/components/reservations/SourceBadge';
import { PaymentTypeControl } from '@/components/frontdesk/PaymentTypeControl';
import { useSearchReservations } from '@/hooks/api/useFrontDesk';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuthContext } from '@/contexts/AuthContext';
import { recentlyViewedKey, pushRecentlyViewed } from '@/lib/recentlyViewed';
import type { BookingEntry } from '@/services/api/frontdesk.api';

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Channel-payload surfacing ───────────────────────────────────────────────
//
// A channel booking (AxisRooms API-9) carries a rich raw payload that the
// normalized booking columns don't fully model. `channelPayload` retains it
// verbatim; here we defensively read both the nested AxisRooms envelope
// (BookingDetails / CheckinDetails / GuestDetails / Rates) AND a flat fallback,
// rendering only the fields that are actually present.

type Payload = Record<string, unknown>;

function asRecord(v: unknown): Payload | undefined {
  return v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Payload)
    : undefined;
}

/** First value that is neither null/undefined nor an empty string. */
function firstDefined(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v != null && v !== '') return v;
  }
  return undefined;
}

function toStr(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  return String(v);
}

/** Format a possibly-stringified amount as money in the booking currency. */
function fmtMoney(v: unknown, currency: string): string | undefined {
  if (v == null || v === '') return undefined;
  const n =
    typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
  if (!Number.isFinite(n)) return toStr(v);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'INR',
    }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

/** Render a truthy-ish flag ("Paid?") as Yes / No, else the raw value. */
function fmtBool(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'paid'].includes(s)) return 'Yes';
  if (['false', '0', 'no', 'n', 'unpaid'].includes(s)) return 'No';
  return String(v);
}

interface ChannelRow {
  label: string;
  value: string;
}

interface ChannelDetails {
  info: ChannelRow[];
  payment: ChannelRow[];
  stay: ChannelRow[];
}

function extractChannelDetails(
  payload: Payload,
  currency: string,
): ChannelDetails {
  // Nested AxisRooms envelope.
  const bd = asRecord(payload.BookingDetails) ?? {};
  const cd = asRecord(payload.CheckinDetails) ?? {};
  const rates = asRecord(payload.Rates);
  const roomTypeArr = Array.isArray(rates?.roomType) ? rates!.roomType : [];
  const room = asRecord(roomTypeArr[0]) ?? {};
  // Flat fallback: some channels send { booking: {...} } or a flat body.
  const flat = asRecord(payload.booking) ?? payload;

  const push = (
    rows: ChannelRow[],
    label: string,
    value: string | undefined,
  ) => {
    if (value != null && value !== '') rows.push({ label, value });
  };

  const info: ChannelRow[] = [];
  push(
    info,
    'Channel / OTA',
    toStr(firstDefined(bd.ota, bd.bookingSource, flat.ota, flat.channel)),
  );
  push(info, 'OTA reference', toStr(firstDefined(bd.otaRefId, flat.otaRefId)));
  push(
    info,
    'Channel booking no.',
    toStr(firstDefined(bd.bookingNo, flat.bookingNo, flat.externalId)),
  );
  push(
    info,
    'Booked by',
    toStr(firstDefined(bd.bookedBy, flat.bookedBy)),
  );
  push(
    info,
    'Booked at',
    toStr(firstDefined(bd.bookingDateTime, flat.bookingDateTime)),
  );

  const payment: ChannelRow[] = [];
  push(payment, 'Paid?', fmtBool(firstDefined(cd.paid, flat.paid)));
  push(
    payment,
    'Amount to collect',
    fmtMoney(
      firstDefined(cd.amountToBeCollected, flat.amountToBeCollected),
      currency,
    ),
  );
  push(
    payment,
    'Supplier amount',
    fmtMoney(firstDefined(cd.supplierAmount, flat.supplierAmount), currency),
  );
  push(
    payment,
    'Taxes',
    fmtMoney(firstDefined(cd.taxes, flat.taxes), currency),
  );
  push(
    payment,
    'Total',
    fmtMoney(firstDefined(cd.totalAmount, flat.totalAmount), currency),
  );

  const stay: ChannelRow[] = [];
  push(
    stay,
    'Rate plan',
    toStr(firstDefined(room.ratePlanName, flat.ratePlanName)),
  );
  push(stay, 'Rooms', toStr(firstDefined(room.noOfRooms, flat.noOfRooms)));
  push(stay, 'Pax', toStr(firstDefined(cd.totalPax, flat.totalPax)));
  push(stay, 'Children', toStr(firstDefined(cd.children, flat.children)));
  const specialReq = firstDefined(cd.specialRequest, flat.specialRequest);
  push(
    stay,
    'Special requests',
    Array.isArray(specialReq)
      ? specialReq.filter((s) => s != null && s !== '').join('; ') || undefined
      : toStr(specialReq),
  );

  return { info, payment, stay };
}

/**
 * "Channel details" panel — surfaces every field an OTA (AxisRooms) push
 * carried. Only rendered for channel-sourced bookings that have a retained
 * `channelPayload`; skipped for direct / storefront bookings.
 */
function ChannelDetailsSection({ booking }: { booking: BookingEntry }) {
  const payload = booking.channelPayload;
  const details = useMemo(
    () =>
      payload && typeof payload === 'object'
        ? extractChannelDetails(payload as Payload, booking.currency)
        : null,
    [payload, booking.currency],
  );

  if (!payload || !details) return null;
  const { info, payment, stay } = details;
  const hasAnything =
    info.length > 0 || payment.length > 0 || stay.length > 0;

  const RowGrid = ({ rows }: { rows: ChannelRow[] }) => (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
      {rows.map((r) => (
        <div key={r.label}>
          <span className="text-gray-500">{r.label}</span>
          <p className="font-medium text-gray-900 break-words">{r.value}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 p-3 space-y-4">
      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
        Channel details
      </span>

      {info.length > 0 && <RowGrid rows={info} />}

      {payment.length > 0 && (
        <div className="space-y-2">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Payment
          </span>
          <RowGrid rows={payment} />
        </div>
      )}

      {stay.length > 0 && (
        <div className="space-y-2">
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Stay
          </span>
          <RowGrid rows={stay} />
        </div>
      )}

      {!hasAnything && (
        <p className="text-sm text-gray-500">
          No recognizable channel fields — see the raw payload below.
        </p>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
          Raw channel payload
        </summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-700">
          {JSON.stringify(payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/**
 * The per-night rate the OTA charged the guest, from the retained booking
 * payload (`Rates.roomType[0].dailyPrice`), formatted in the booking currency.
 * Independent of the sync snapshot — this is what the guest was actually
 * charged. Undefined for direct bookings or when the payload carries no rate.
 */
function otaNightly(booking: BookingEntry): string | undefined {
  const payload = asRecord(booking.channelPayload);
  if (!payload) return undefined;
  const rates = asRecord(payload.Rates);
  const roomTypeArr = Array.isArray(rates?.roomType) ? rates!.roomType : [];
  const room = asRecord(roomTypeArr[0]) ?? {};
  const flat = asRecord(payload.booking) ?? payload;
  const raw = firstDefined(room.dailyPrice, room.dailyRate, flat.dailyPrice);
  return fmtMoney(raw, booking.currency);
}

/**
 * Party composition, OTA-style. Prefers the adults/children breakdown captured
 * on the public site; falls back to the flat guest count when unavailable.
 */
function fmtGuests(booking: BookingEntry): string {
  if (booking.adults == null) return String(booking.guestsCount);
  const parts = [`${booking.adults} adult${booking.adults === 1 ? '' : 's'}`];
  if (booking.children != null && booking.children > 0) {
    parts.push(`${booking.children} child${booking.children === 1 ? '' : 'ren'}`);
  }
  return parts.join(' · ');
}

/**
 * Lightweight booking-detail panel for ReservationsPage (FE-Q1 option a).
 *
 * Fetches a single booking via the existing front-desk search-by-id path (no
 * `/bookings/:id` route exists). When the booking resolves it records it into
 * the Recently-Viewed localStorage list, which the FrontDeskPage panel reads.
 */
export function BookingDetailSlideOver({
  bookingId,
  onClose,
}: {
  bookingId: string | null;
  onClose: () => void;
}) {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuthContext();

  const query = useSearchReservations(
    { bookingId: bookingId ?? undefined, propertyId: propertyId || undefined },
    bookingId !== null,
  );

  const booking: BookingEntry | undefined = useMemo(() => {
    const list = query.data?.data ?? [];
    return list.find((b) => b.id === bookingId) ?? list[0];
  }, [query.data, bookingId]);

  // Record into Recently Viewed once the booking resolves.
  useEffect(() => {
    if (!booking) return;
    const key = recentlyViewedKey(user?.tenantId, propertyId);
    pushRecentlyViewed(key, {
      id: booking.id,
      guestName: booking.guest?.fullName ?? 'Unknown Guest',
      unitName: booking.unit?.name ?? booking.unitId.slice(0, 8),
      checkIn: booking.checkIn,
      checkOut: booking.checkOut,
      viewedAt: Date.now(),
    });
  }, [booking, user?.tenantId, propertyId]);

  return (
    <SlideOver
      isOpen={bookingId !== null}
      onClose={onClose}
      title="Reservation Detail"
      width="max-w-lg"
    >
      {query.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : !booking ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          This reservation could not be found. It may have been removed or belongs to another property.
        </p>
      ) : (
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {booking.guest?.fullName ?? 'Unknown Guest'}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={booking.status} />
              <PaymentTypeBadge paymentType={booking.paymentType} />
              <SourceBadge
                source={booking.source}
                channelPayload={booking.channelPayload}
                externalBookingId={booking.externalBookingId}
              />
            </div>
          </div>

          {/* Payment type set-control */}
          <div className="rounded-xl border border-gray-200 p-3">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Payment
            </span>
            <PaymentTypeControl bookingId={booking.id} paymentType={booking.paymentType} />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Unit</span>
              <p className="font-medium text-gray-900">{booking.unit?.name ?? booking.unitId.slice(0, 8)}</p>
            </div>
            <div>
              <span className="text-gray-500">Source</span>
              <p className="font-medium text-gray-900 capitalize">{booking.source}</p>
            </div>
            <div>
              <span className="text-gray-500">Check-in</span>
              <p className="font-medium text-gray-900">{fmtDate(booking.checkIn)}</p>
            </div>
            <div>
              <span className="text-gray-500">Check-out</span>
              <p className="font-medium text-gray-900">{fmtDate(booking.checkOut)}</p>
            </div>
            <div>
              <span className="text-gray-500">Guests</span>
              <p className="font-medium text-gray-900">{fmtGuests(booking)}</p>
            </div>
            <div>
              <span className="text-gray-500">Total</span>
              <p className="font-medium text-gray-900">${booking.totalAmount}</p>
            </div>
            {otaNightly(booking) && (
              <div>
                <span className="text-gray-500">OTA-charged nightly</span>
                <p className="font-medium text-gray-900">{otaNightly(booking)}</p>
              </div>
            )}
          </div>

          {booking.guest?.email && (
            <div className="text-sm">
              <span className="text-gray-500">Email</span>
              <p className="font-medium text-gray-900">{booking.guest.email}</p>
            </div>
          )}
          {booking.guest?.phone && (
            <div className="text-sm">
              <span className="text-gray-500">Phone</span>
              <p className="font-medium text-gray-900">{booking.guest.phone}</p>
            </div>
          )}
          {booking.guestNames && (
            <div className="text-sm">
              <span className="text-gray-500">Names of guests</span>
              <p className="mt-1 font-medium text-gray-900 whitespace-pre-wrap">{booking.guestNames}</p>
            </div>
          )}
          {booking.arrivalTime && (
            <div className="text-sm">
              <span className="text-gray-500">Estimated arrival</span>
              <p className="font-medium text-gray-900">{booking.arrivalTime}</p>
            </div>
          )}
          {booking.specialRequests && (
            <div className="text-sm">
              <span className="text-gray-500">Special Requests</span>
              <p className="mt-1 text-gray-700 whitespace-pre-wrap">{booking.specialRequests}</p>
            </div>
          )}

          <ChannelDetailsSection booking={booking} />
        </div>
      )}
    </SlideOver>
  );
}
